import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { FeaturedVideo } from '@/shared/types';
import { Button, ChevronRightIcon, EmptyState, Spinner } from '@/shared/ui';
import { BrowseCard } from '@/features/browse/components/BrowseCard';
import { fetchShowcaseVideos } from '@/features/browse/services/showcaseService';
import { styleCopy } from '@/features/landing/services/styleSections';

/**
 * Every video of one render style — where a band's "See all" leads.
 *
 * A route rather than a modal, so the URL names what you are looking at: the link
 * is shareable, the back button leaves, and the rail can show the section as
 * current. That is the behaviour the reference (openart.ai) has and the reason its
 * sidebar highlights while you browse a category.
 *
 * Fetches the same public feed the landing page does and filters client-side. One
 * endpoint, one shape, no `?style=` parameter to keep in step — the feed is a
 * page's worth of rows, not a catalogue that needs paging, and the browser has
 * usually cached it from the landing page a click earlier.
 */
export default function ShowcaseStylePage() {
  const { style = '' } = useParams();
  const navigate = useNavigate();
  const [videos, setVideos] = useState<FeaturedVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(false);
    void fetchShowcaseVideos()
      .then(setVideos)
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const copy = styleCopy(style);
  const shown = useMemo(() => videos.filter((video) => video.style === style), [videos, style]);

  /* Signed out, every card action is an invitation rather than a feature: there is
     no library to save into and no player behind the wall. */
  const inviteSignIn = () => navigate('/login');

  return (
    <section className="showcase-all">
      <nav className="showcase-all__crumbs" aria-label="Breadcrumb">
        <Link to="/templates">Templates</Link>
        <ChevronRightIcon size={13} aria-hidden />
        <span aria-current="page">{copy.heading}</span>
      </nav>

      <header className="showcase-all__head">
        <h1 className="showcase-all__title">{copy.heading}</h1>
        <p className="showcase-all__blurb">{copy.blurb}</p>
      </header>

      {loading ? (
        <div className="showcase-all__state">
          <Spinner size={26} label={`Loading ${copy.heading} videos`} />
        </div>
      ) : loadError ? (
        <div className="showcase-all__state" role="alert">
          <EmptyState
            headingLevel={2}
            title="Couldn’t load these videos"
            hint="Please check your connection and try again."
            action={
              <Button variant="secondary" onClick={load}>
                Try again
              </Button>
            }
          />
        </div>
      ) : shown.length === 0 ? (
        <div className="showcase-all__state">
          <EmptyState
            headingLevel={2}
            title={`Nothing in ${copy.heading} yet`}
            hint="Check back soon for new videos."
          />
        </div>
      ) : (
        /* Same grid as a band, same motion cards — this is the band with its cap
           lifted, not a different presentation of the same videos. */
        <div className="style-band__wall">
          {shown.map((video) => (
            <BrowseCard
              key={video.id}
              video={video}
              active={false}
              saved={false}
              onSelect={inviteSignIn}
              onToggleSave={inviteSignIn}
              motion
            />
          ))}
        </div>
      )}
    </section>
  );
}
